import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AssetToken } from '@/types/models';

/**
 * Fetch asset token by asset ID
 */
export function useAssetToken(assetId: string | undefined) {
  return useQuery({
    queryKey: ['asset-token', assetId],
    queryFn: async () => {
      if (!assetId) return null;

      const { data, error } = await supabase
        .from('asset_tokens')
        .select('*')
        .eq('asset_id', assetId)
        .single();

      if (error) throw error;
      return data as AssetToken;
    },
    enabled: !!assetId,
  });
}

/**
 * Fetch asset by token (public - works for unauthenticated users)
 */
export async function fetchAssetByToken(token: string) {
  // First get the asset_id from the token
  const { data: tokenData, error: tokenError } = await supabase
    .from('asset_tokens')
    .select('asset_id, organization_id')
    .eq('token', token)
    .single();

  if (tokenError || !tokenData) {
    throw new Error('Invalid or expired token');
  }

  // Then fetch the asset with related data
  const { data: assetData, error: assetError } = await supabase
    .from('assets')
    .select(`
      *,
      location:locations(*),
      category:categories(*),
      department:departments(*),
      assigned_to:person_assets(
        person:people(*)
      )
    `)
    .eq('id', tokenData.asset_id)
    .single();

  if (assetError) throw assetError;

  // Record the scan
  await supabase.rpc('record_asset_scan', { token_value: token });

  return assetData;
}

/**
 * Hook to fetch asset by token
 */
export function useAssetByToken(token: string | undefined) {
  return useQuery({
    queryKey: ['asset-by-token', token],
    queryFn: () => fetchAssetByToken(token!),
    enabled: !!token,
    staleTime: 0, // Always refetch to get latest data
  });
}

/**
 * Generate token for an asset (creates if doesn't exist)
 */
export function useGenerateAssetToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      // Check if token already exists
      const { data: existing } = await supabase
        .from('asset_tokens')
        .select('*')
        .eq('asset_id', assetId)
        .single();

      if (existing) {
        return existing as AssetToken;
      }

      // Generate new token (trigger will create it automatically on first insert)
      // But we can also explicitly create it here
      const { data, error } = await supabase
        .from('asset_tokens')
        .insert({ asset_id: assetId })
        .select()
        .single();

      if (error) throw error;
      return data as AssetToken;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-token', data.asset_id] });
    },
  });
}

/**
 * Mark label as printed
 */
export function useMarkLabelPrinted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokenId: string) => {
      const { data, error } = await supabase
        .from('asset_tokens')
        .update({
          label_printed: true,
          label_printed_at: new Date().toISOString(),
        })
        .eq('id', tokenId)
        .select()
        .single();

      if (error) throw error;
      return data as AssetToken;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-token', data.asset_id] });
    },
  });
}

/**
 * Regenerate token for an asset
 */
export function useRegenerateAssetToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assetId: string) => {
      // Delete old token
      await supabase
        .from('asset_tokens')
        .delete()
        .eq('asset_id', assetId);

      // Trigger will create new token automatically, or we can create manually
      const { data, error } = await supabase.rpc('generate_asset_token');

      if (error) throw error;

      // Create new token record
      const { data: newToken, error: createError } = await supabase
        .from('asset_tokens')
        .insert({ asset_id: assetId, token: data })
        .select()
        .single();

      if (createError) throw createError;
      return newToken as AssetToken;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['asset-token', data.asset_id] });
    },
  });
}
